<template>
  <Teleport
    v-if="isSearchModalActive || activeSearchTriggerId === null"
    defer
    :to="activeSearchTriggerId ? `#search-teleport-${activeSearchTriggerId}` : 'body'"
  >
    <div
      v-show="activeSearchTriggerId"
      ref="searchModalEl"
      class="nav-search-modal"
      :class="{
        'mew-search-modal-clean': !isDesktop && killswitches.masterMewWhitespaceRemovalEnabled,
        'is-empty': isContentEmpty,
      }"
      @keydown.esc="handleCloseModal"
    >
      <SearchModalContent
        ref="modalContentEl"
        :is-keyboard-open="isKeyboardOpen"
        :focus-search-input="focusSearchInput"
        @is-empty="isContentEmpty = $event"
      />
      <button
        class="button button-small secondary expanded"
        :class="{ 'search-close--hidden': !isCloseButtonFocused, 'margin-top-s': isCloseButtonFocused }"
        aria-label="close search dropdown"
        @focus="isCloseButtonFocused = true"
        @blur="isCloseButtonFocused = false"
        @click="handleCloseModal"
      >
        Close
      </button>
    </div>
  </Teleport>
</template>

<script setup>
/* v8 ignore file -- @preserve */
import { ref, computed, watch, onMounted, nextTick } from 'vue';
import { useEventBus } from '@vueuse/core';
import { useFocusTrap } from '~/composables/dumb/useFocusTrap';
import useScrollLock from '~/composables/ui/useScrollLock';
import { useSearchModalInput } from '~/composables/accessibility/useSearchModalInput';
import useKillswitches from '~/composables/useKillswitches';
import { OVERLAY, events, SEARCH } from '#shared/constants/eventBus';
import { SEARCH_MODAL_OVERLAY } from '#shared/constants/common/overlayNames';
import { RESET_OVERLAY, SET_ACTIVE_OVERLAY_NAME, SET_SEARCH_MODAL_STATE } from '#shared/types/actions';
import { GET_IS_DESKTOP_VIEW } from '#shared/types/getters';

import SearchModalContent from './SearchModalContent.common';

const navHeader = useNavHeader();
const navViewType = useNavViewType();
const navOverlay = useNavOverlay();
const killswitches = useKillswitches();

const KEYBOARD_OPEN_RATIO = 0.85;
const searchModalEl = ref(null);
const modalContentEl = ref(null);
const isContentEmpty = ref(true);
const isCloseButtonFocused = ref(false);

const trapContainer = computed(() => {
  return searchModalEl.value?.closest('.search-modal-transform');
});

const isDesktop = computed(() => navViewType[GET_IS_DESKTOP_VIEW]);
const isSearchModalActive = computed(() => navHeader.isSearchModalActive);
const activeSearchTriggerId = computed(() => navHeader.activeSearchTriggerId);

const { lock, unlock } = useScrollLock();
const { activate, deactivate } = useFocusTrap(trapContainer);
const overlayBus = useEventBus(OVERLAY);
const searchBus = useEventBus(SEARCH);

// useSearchModalInput expects refs to DOM elements, we map them dynamically
const suggestionsEl = computed(() => modalContentEl.value?.suggestionsEl);
const historyEl = computed(() => modalContentEl.value?.historyEl);
const { onKeydownArrowDown } = useSearchModalInput({ suggestionsEl, historyEl });

function focusSearchInput() {
  const globalSearchInput = document.querySelector('.nav-search-input .input-search');

  if (globalSearchInput) {
    globalSearchInput.focus();
  }
}

function handleOpenModal() {
  if (isDesktop.value) {
    window.scrollTo(0, 0);
  }

  lock();
  navOverlay[SET_ACTIVE_OVERLAY_NAME](SEARCH_MODAL_OVERLAY);
  navHeader[SET_SEARCH_MODAL_STATE](true);

  nextTick(() => {
    activate();
  });

  setTimeout(() => {
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', checkKeyboard);
    }
  }, 100);
}

function handleCloseModal() {
  unlock();
  navOverlay[RESET_OVERLAY]();
  navHeader[SET_SEARCH_MODAL_STATE](false);
  deactivate();
  isCloseButtonFocused.value = false;
  if (window.visualViewport) {
    window.visualViewport.removeEventListener('resize', checkKeyboard);
  }
}

const isKeyboardOpen = ref(false);

const checkKeyboard = () => {
  if (!window.visualViewport) return;
  isKeyboardOpen.value = window.visualViewport.height < window.innerHeight * KEYBOARD_OPEN_RATIO;
};

onMounted(() => {
  watch(isSearchModalActive, (newValue, oldValue) => {
    if (newValue) {
      handleOpenModal();
    } else if (newValue !== oldValue) {
      handleCloseModal();
    }
  });
  overlayBus.on((event) => {
    if (event === events[OVERLAY].CLICK) handleCloseModal();
  });
  searchBus.on((event) => {
    if (event === events[SEARCH].CLOSE) handleCloseModal();
    else if (event === events[SEARCH].KEYDOWN_ARROW_DOWN) onKeydownArrowDown();
  });
});
</script>

<style lang="scss">
.nav-search-modal {
  width: 100%;
  background-color: #fff;
  padding: 32px 40px;
  position: relative;
  top: 0;

  @include breakpoint(medium down) {
    padding: 24px 0 0;
  }

  .search-close--hidden {
    position: absolute;
    height: 0;
    border: none;
    opacity: 0;
    overflow: hidden;
    pointer-events: none;
  }
}
</style>

<style lang="scss" brand="mcom">
.nav-search-modal {
  border-radius: 24px;
}

.mew-search-modal-clean {
  border-radius: 10px;
  animation: search-modal-animation 600ms forwards;
}

@keyframes search-modal-animation {
  0% {
    opacity: 0;
    transform: translateY(0);
  }

  30% {
    opacity: 0;
    transform: translateY(0);
  }

  85% {
    animation-timing-function: cubic-bezier(0.5, 0, 0.5, 1);
    opacity: 1;
  }

  100% {
    animation-timing-function: cubic-bezier(0.22, 1, 0.36, 1);
    opacity: 1;
    transform: translateY(8px);
  }
}

.nav-search-modal.mew-search-modal-clean {
  padding: 24px 16px 32px;
}
</style>

<style lang="scss" brand="bcom">
@include breakpoint(large up) {
  .nav-search-modal.is-empty {
    padding: 8px 40px 0;
  }
}

.nav-search-modal.mew-search-modal-clean {
  padding: 23px 15px 32px;
  border: 1px solid $gray-4-color;
  box-shadow: 1px 1px 2px 0 rgba(0, 0, 0, 0.1);
}

.mew-search-modal-clean {
  animation: search-modal-animation 550ms forwards;

  &.is-empty {
    display: none;
  }
}

@keyframes search-modal-animation {
  0% {
    opacity: 0;
    transform: translateY(0);
  }

  36% {
    opacity: 0;
    transform: translateY(0);
  }

  95% {
    animation-timing-function: cubic-bezier(0.5, 0, 0.5, 1);
    opacity: 1;
  }

  100% {
    animation-timing-function: cubic-bezier(0.22, 1, 0.36, 1);
    opacity: 1;
    transform: translateY(8px);
  }
}
</style>
